import React, { useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';

import SectionLoader from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import WorkspaceTaskEntry, {
  WorkspaceTaskEntryHeader,
} from '@/src/components/common/CustomCalendar/CalendarEntry/WorkspaceTaskEntry';

import { Task } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/types/task';

import NoTask from './NoTask';

interface WorkspaceTodosViewProps {
  tasks: Task[];
  queryInfo: any;
  isReportsEnabled?: boolean;
}

const WorkspaceTodosView: React.FC<WorkspaceTodosViewProps> = ({
  tasks,
  queryInfo,
  isReportsEnabled,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastRowRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersect = useCallback(
    (entries) => {
      if (
        entries[0].isIntersecting &&
        queryInfo.hasNextPage &&
        !queryInfo.isFetchingNextPage &&
        !queryInfo.isRefetching
      ) {
        queryInfo.fetchNextPage();
      }
    },
    [queryInfo]
  );

  useEffect(() => {
    if (!lastRowRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(handleIntersect, { threshold: 0.7 });
    observerRef.current.observe(lastRowRef.current);
    return () => observerRef.current?.disconnect();
  }, [tasks, handleIntersect]);

  const { projectId } = useParams<{ projectId: string }>();

  return (
    <>
      {queryInfo.isLoading && tasks.length === 0 ? (
        <SectionLoader className="tw-rounded-lg" />
      ) : tasks.length > 0 ? (
        <>
          {!isReportsEnabled && <WorkspaceTaskEntryHeader />}
          {tasks.map((task, idx) => {
            const isLast = idx === tasks.length - 1;
            return (
              <div key={task.taskId || idx} ref={isLast ? lastRowRef : undefined}>
                <WorkspaceTaskEntry task={task} compactWidth={isReportsEnabled} />
              </div>
            );
          })}
          {(queryInfo.isFetchingNextPage || queryInfo.isLoading) && (
            <SectionLoader className="tw-rounded-lg tw-bg-transparent" />
          )}
        </>
      ) : (
        <NoTask showProcessesLink projectId={projectId} />
      )}
    </>
  );
};

export default WorkspaceTodosView;
